// ui/components/debug/DashboardTodosDebugTab.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useToast } from "../Toast";

import { db } from "../../../firebaseClient";
import {
  getTrueTodoKpiSummaryOnce,
  triggerRebuildTrueTodoKpiSummaryNow,
  type TrueTodoKpiSummary,
} from "../../services/trueTodoKpiSummaryService";
import { getTrueTodosOnce } from "../../services/trueTodoAggregator";
import type { TrueTodoItem, TrueTodoExcluded } from "../../services/trueTodoAdapter";
import type { PendingModule } from "../../services/dailySummaryService";
import { computeTrueDashboardSummary } from "../../services/todosSummary";

type UserKey = "kevin" | "ops" | "gm";

type GhostCandidate = {
  key: string;
  collection: "todos" | "approvalFlows" | "approval_requests";
  id: string;
  targetId?: string;
  source: string;
  reason?: string;
  message?: string;
  moduleRaw?: string;
  docNo?: string;
  title?: string;
  autoSafe?: boolean;
};

function n(v: any): number {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

function fmt(v: any): string {
  const x = n(v);
  return x.toLocaleString();
}

function userLabel(k: UserKey): string {
  if (k === "kevin") return "法務（你）";
  if (k === "ops") return "營運長";
  return "總經理";
}

function moduleLabel(m: PendingModule): string {
  switch (m) {
    case "contractReviews":
      return "合約審理";
    case "templates":
      return "制式合約";
    case "seals":
      return "用印申請";
    case "govCorrespondence":
      return "公文函覆";
    case "projectControl":
      return "專案管控";
    case "regIntel":
      return "法遵情資";
    case "governanceWorkspace":
      return "核心文件工作區";
    case "cases":
      return "案件/爭議";
    default:
      return String(m);
  }
}

function normalizeModule(v: any): string {
  return String(v || "").trim().toUpperCase();
}


const isDemoId = (id?: string | null) => String(id || "").toUpperCase().includes("DEMO");

/**
 * 嚴格分類：只把「確定能對應到功能模組」的資料納入儀錶板三人待辦
 * 其他一律回傳 null（避免幽靈待辦）
 */
function classifyModuleStrict(raw: any): PendingModule | null {
  const upper = normalizeModule(raw);
  if (!upper) return null;

  // 合約審理
  if (
    upper === "CONTRACT_REVIEW" ||
    upper === "CONTRACT_REVIEWS" ||
    upper.includes("CONTRACT_REVIEW") ||
    upper.includes("CONTRACT")
  ) {
    return "contractReviews";
  }

  // 制式合約
  if (
    upper === "TEMPLATE" ||
    upper === "TEMPLATES" ||
    upper.includes("TEMPLATE") ||
    upper.includes("STANDARD_CONTRACT")
  ) {
    return "templates";
  }

  // 用印申請
  if (
    upper === "SEAL_REQUEST" ||
    upper === "SEAL" ||
    upper.includes("SEAL") ||
    upper.includes("SIGN_SYSTEM") ||
    upper.includes("SIGN")
  ) {
    return "seals";
  }

  // 公文函覆
  if (
    upper === "OFFICIAL_REPLY" ||
    upper === "GOV_CORRESPONDENCE" ||
    upper.includes("GOV_CORRESP") ||
    upper.includes("OFFICIAL")
  ) {
    return "govCorrespondence";
  }

  // 法遵情資
  if (upper === "LAW_INFO" || upper.includes("LAW_INFO") || upper.includes("INTEL") || upper.includes("COMPLIANCE")) {
    return "regIntel";
  }

  // 專案管控
  if (upper === "PROJECT_CONTROL" || upper.includes("PROJECT_CONTROL") || upper.includes("PROJECT")) {
    return "projectControl";
  }

  // 核心文件工作區
  if (
    upper === "CORE_DOC" ||
    upper === "GOVERNANCE_CORE_DOC" ||
    upper.includes("CORE_DOC") ||
    upper.includes("GOVERNANCE_CORE") ||
    upper.includes("GOV_FILES")
  ) {
    return "governanceWorkspace";
  }

  // 案件與爭議（如果後續也納入簽核流轉）
  if (
    upper === "CASES" ||
    upper === "CASE" ||
    upper.includes("CASE") ||
    upper.includes("DISPUTE") ||
    upper.includes("LITIGATION")
  ) {
    return "cases";
  }

  return null;
}

function uuidLike(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);
}

// Firestore autoId (20 chars alnum) / legacy random IDs
function firestoreAutoIdLike(s: string): boolean {
  const t = String(s || "").trim();
  if (t.length < 18 || t.length > 28) return false;
  if (!/^[A-Za-z0-9]+$/.test(t)) return false;
  // 至少同時含英文字與數字，避免誤判純字串/純數字
  return /[A-Za-z]/.test(t) && /[0-9]/.test(t);
}

function digitsOnly(s: string): boolean {
  const t = String(s || "").trim();
  return t.length > 0 && t.length <= 8 && /^\d+$/.test(t);
}

function placeholderTitleLike(s: string): boolean {
  const t = String(s || "").trim().toUpperCase();
  if (!t) return true;
  if (["AAA", "BBB", "CCC", "TEST", "TBD", "TODO"].includes(t)) return true;
  if (t.startsWith("TEST ") || t.startsWith("TEST_") || t.startsWith("TEST-")) return true;
  if (t.startsWith("SAMPLE") || t.startsWith("DUMMY")) return true;
  if (digitsOnly(t)) return true; // 12 / 123 這種也常是 placeholder
  return false;
}

function invalidDocNoLike(s: string): boolean {
  const t = String(s || "").trim();
  if (!t) return true;
  if (digitsOnly(t)) return true;
  if (uuidLike(t)) return true;
  if (firestoreAutoIdLike(t)) return true;
  return false;
}

function StatChip({ label, value, tone = "neutral" }: { label: string; value: any; tone?: "neutral" | "good" | "bad" }) {
  const bg = tone === "good" ? "#10351e" : tone === "bad" ? "#3a1212" : "#1f1f1f";
  const bd = tone === "good" ? "#1f7a3a" : tone === "bad" ? "#a33b3b" : "#333";
  return (
    <span
      style={{
        display: "inline-flex",
        gap: 8,
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: 999,
        background: bg,
        border: `1px solid ${bd}`,
        fontSize: 12,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ opacity: 0.75 }}>{label}</span>
      <b style={{ fontSize: 13 }}>{fmt(value)}</b>
    </span>
  );
}

const DashboardTodosDebugTab: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [serverKpi, setServerKpi] = useState<TrueTodoKpiSummary | null>(null);

  const [trueTodos, setTrueTodos] = useState<TrueTodoItem[]>([]);
  const [excluded, setExcluded] = useState<TrueTodoExcluded[]>([]);

  const [approvalPendingCounts, setApprovalPendingCounts] = useState<Record<string, number>>({});
  const [ghostCandidates, setGhostCandidates] = useState<GhostCandidate[]>([]);
  const [selectedGhostKeys, setSelectedGhostKeys] = useState<Record<string, boolean>>({});

  // 破壞性操作：預設鎖住，避免手滑
  const [deleteUnlocked, setDeleteUnlocked] = useState(false);

  const [q, setQ] = useState<{ kevin: string; ops: string; gm: string }>({ kevin: "", ops: "", gm: "" });

  const toastCtx = useToast();
  const toast = (message: string, kind?: string) => {
    const raw = String(kind || "info").toLowerCase();
    const mapped = raw === "warn" || raw === "warning" ? "warning" : raw === "success" ? "success" : raw === "error" ? "error" : "info";
    toastCtx.showToast(message, mapped as any);
  };


  const strictTodos = useMemo(() => {
    return (trueTodos || []).filter((t) => {
      const src = String((t as any)?.source || "");
      if (src === "RAW_TODO") return false;

      const targets = Array.isArray((t as any)?.targetUsers) ? (t as any).targetUsers : [];
      if (!targets.length) return false;

      const mod = classifyModuleStrict((t as any)?.module || (t as any)?.moduleKey || (t as any)?.sourceModule);
      if (!mod) return false;

      return true;
    });
  }, [trueTodos]);

  const byUser = useMemo(() => {
    const out: Record<UserKey, TrueTodoItem[]> = { kevin: [], ops: [], gm: [] };
    for (const t of strictTodos) {
      const targets = Array.isArray((t as any)?.targetUsers) ? (t as any).targetUsers : [];
      const keys = Array.from(
        new Set(
          targets
            .map((x: any) => (typeof x === "string" ? x : x?.userKey))
            .map((x: any) => String(x || "").trim().toLowerCase())
            .filter((x: string) => x === "kevin" || x === "ops" || x === "gm")
        )
      ) as UserKey[];

      for (const uk of keys) out[uk].push(t);
    }

    // 排序：module → docNo
    const sortKey = (t: any) => `${normalizeModule(t?.module)}::${String(t?.docNo || t?.id || "")}`;
    (Object.keys(out) as UserKey[]).forEach((k) => out[k].sort((a, b) => (sortKey(a) < sortKey(b) ? -1 : 1)));
    return out;
  }, [strictTodos]);

  const strictCounts = useMemo(() => {
    const countsByUser: Record<UserKey, number> = { kevin: byUser.kevin.length, ops: byUser.ops.length, gm: byUser.gm.length };

    const countsByModule: Record<PendingModule, number> = {
      contractReviews: 0,
      templates: 0,
      seals: 0,
      cases: 0,
      govCorrespondence: 0,
      regIntel: 0,
      projectControl: 0,
      governanceWorkspace: 0,
    };

    for (const t of strictTodos) {
      const mod = classifyModuleStrict((t as any)?.module || (t as any)?.moduleKey || (t as any)?.sourceModule);
      if (!mod) continue;
      countsByModule[mod] = (countsByModule[mod] || 0) + 1;
    }

    return { countsByUser, countsByModule, total: strictTodos.length };
  }, [byUser, strictTodos]);

  const diffSummary = useMemo(() => {
    const allMods = Object.keys(strictCounts.countsByModule) as PendingModule[];
    let worst: { module: PendingModule | null; diff: number; strict: number; requests: number } = {
      module: null,
      diff: 0,
      strict: 0,
      requests: 0,
    };

    let ok = true;

    for (const m of allMods) {
      const s = n(strictCounts.countsByModule[m]);
      const r = n(approvalPendingCounts[m]);
      const d = s - r;
      if (d !== 0) ok = false;
      if (Math.abs(d) > Math.abs(worst.diff)) worst = { module: m, diff: d, strict: s, requests: r };
    }

    return { ok, worst };
  }, [strictCounts, approvalPendingCounts]);

  const recomputeClientDaily = useMemo(() => {
    // 直接跑一遍與儀錶板同管線（方便對照）
    try {
      return computeTrueDashboardSummary(trueTodos);
    } catch {
      return null;
    }
  }, [trueTodos]);

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const kpi = await getTrueTodoKpiSummaryOnce();
      setServerKpi(kpi || null);

      const { trueTodos: tt, excluded: ex } = await getTrueTodosOnce({ limitPerSource: 5000 });
      setTrueTodos(tt || []);
      setExcluded(ex || []);

      // approval_requests pending counts (module-only)
      const snap = await (db as any).collection("approval_requests").where("status", "==", "PENDING").get();
      const counts: Record<string, number> = {};
      snap.forEach((d: any) => {
        const raw = d.data?.() || d.data || {};
        if (raw?.isDeleted) return;
        const mod = classifyModuleStrict(raw?.moduleKey || raw?.module || raw?.approvalModule || raw?.type);
        if (!mod) return;
        counts[mod] = (counts[mod] || 0) + 1;
      });
      setApprovalPendingCounts(counts);

      // build ghost candidates
      const ghosts: GhostCandidate[] = [];

      // A) excluded list from adapters (強烈懷疑是幽靈/不完整)
      for (const x of ex || []) {
        const src = String((x as any)?.source || "");
        const id = String((x as any)?.id || "");
        if (!id) continue;

        let collection: GhostCandidate["collection"] = "todos";
        if (src === "APPROVAL_FLOW") collection = "approvalFlows";
        if (src === "APPROVAL_REQUEST") collection = "approval_requests";
        if (src === "RAW_TODO") collection = "todos";

        const reason = String((x as any)?.reason || "");
        const msg = String((x as any)?.message || "");

        const rawAny = (x as any)?.raw || {};
        const computed = (rawAny as any)?.__computed || {};
        const docNo = String(
          computed?.docNo || rawAny?.docNo || rawAny?.docNumber || rawAny?.refNo || rawAny?.targetDocNo || rawAny?.targetId || ""
        );
        const title = String(computed?.title || rawAny?.title || rawAny?.subject || rawAny?.name || rawAny?.targetTitle || "");
        const targetId = String(rawAny?.targetId || rawAny?.approvalRequestId || rawAny?.requestId || rawAny?.targetDocId || "");

        ghosts.push({
          key: `${collection}:${id}`,
          collection,
          id,
          targetId: targetId || undefined,
          source: src || "UNKNOWN",
          reason: reason || undefined,
          message: msg || undefined,
          docNo: docNo || undefined,
          title: title || undefined,
          autoSafe:
            collection === "todos" ||
            (collection === "approvalFlows" && (reason === "MISSING_REQUEST" || reason === "MISSING_TARGET_ID")),
        });
      }

      // B) RAW_TODO 直接列為候選（常見：舊檔/測試檔/已刪檔 UUID）
      for (const t of tt || []) {
        const src = String((t as any)?.source || "");
        if (src !== "RAW_TODO") continue;
        const id = String((t as any)?.rawId || (t as any)?.id || "");
        const docNo = String((t as any)?.docNo || "");
        const title = String((t as any)?.title || "");
        const modRaw = String((t as any)?.module || "");

        const looksGhost =
          invalidDocNoLike(docNo) ||
          uuidLike(id) ||
          placeholderTitleLike(title) ||
          !classifyModuleStrict(modRaw);
        if (!looksGhost) continue;

        ghosts.push({
          key: `todos:${id}`,
          collection: "todos",
          id,
          source: "RAW_TODO",
          reason: "RAW_TODO_CANDIDATE",
          message: "RAW_TODO 常包含舊檔/測試檔/已刪檔索引；建議清除以避免幽靈待辦",
          moduleRaw: modRaw || undefined,
          docNo: docNo || undefined,
          title: title || undefined,
          autoSafe: true,
        });
      }

      // 去重
      const uniq = new Map<string, GhostCandidate>();
      for (const g of ghosts) {
        if (!g?.key) continue;
        if (!uniq.has(g.key)) uniq.set(g.key, g);
      }
      const ghostList = Array.from(uniq.values()).sort((a, b) => (a.key < b.key ? -1 : 1));
      setGhostCandidates(ghostList);

      // init selections: only autosafe
      const sel: Record<string, boolean> = {};
      ghostList.forEach((g) => {
        if (g.autoSafe) sel[g.key] = true;
      });
      setSelectedGhostKeys(sel);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  async function rebuildServer() {
    setRebuilding(true);
    setError(null);
    try {
      const res = await triggerRebuildTrueTodoKpiSummaryNow();
      if (!res?.ok) throw new Error(res?.error?.message || "rebuild failed");
      await loadAll();
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setRebuilding(false);
    }
  }

  function lockDeleteMode() {
    setDeleteUnlocked(false);
    toast("已鎖回：刪除模式關閉", "info");
  }

  function ensureDeleteUnlocked(): boolean {
    if (deleteUnlocked) return true;
    const token = window.prompt("⚠️ 高風險操作：輸入 DELETE 解鎖刪除模式\n（只會刪你勾選的幽靈候選項）");
    if (String(token || "").trim() !== "DELETE") {
      toast("未解鎖：輸入不正確（需要 DELETE）", "warn");
      return false;
    }
    setDeleteUnlocked(true);
    toast("已解鎖：刪除模式已開啟", "success");
    return true;
  }

  async function deleteGhostsByKeys(keys: string[]) {

    const uniq = Array.from(new Set((keys || []).filter(Boolean)));

    const chosenRaw = ghostCandidates.filter((g) => uniq.includes(g.key));

    const demoSkipped = chosenRaw.filter((g) => isDemoId(g.id) || (g.targetId ? isDemoId(g.targetId) : false));

    const chosen = chosenRaw.filter((g) => !isDemoId(g.id) && !(g.targetId ? isDemoId(g.targetId) : false));

    if (!chosen.length) {

      if (demoSkipped.length) toast(`已自動保留 DEMO：${demoSkipped.length} 筆（沒有可刪除項目）`, "info");

      return;

    }


    if (!ensureDeleteUnlocked()) return;


    let confirmMsg =

      chosen.length <= 1

        ? `確定要刪除這筆幽靈？\n${chosen[0].collection}/${chosen[0].id}`

        : `確定要刪除 ${chosen.length} 筆幽靈？（高風險）`;

    if (demoSkipped.length) confirmMsg += `\n（已自動保留 DEMO：${demoSkipped.length} 筆）`;

    if (!window.confirm(confirmMsg)) return;


    setLoading(true);

    setError(null);

    try {

      // 目標：刪除「幽靈待辦」時，連同關聯的 approval_requests / approvalFlows / todos 一起清乾淨

      // ⚠️ Firestore batch 上限 500；這裡用 chunk commit，避免「全選 200+」直接失敗但看起來像沒刪。


      type DeleteOp = { collectionName: string; id: string };

      const ops: DeleteOp[] = [];

      const seen = new Set<string>();


      const addDelete = (collectionName: string, id: string) => {

        if (!collectionName || !id) return;

        const key = `${collectionName}/${id}`;

        if (seen.has(key)) return;

        seen.add(key);

        ops.push({ collectionName, id });

      };


      const deleteByQuery = async (collectionName: string, field: string, value: string) => {

        try {

          const qs = await (db as any).collection(collectionName).where(field, "==", value).limit(100).get();

          for (const d of qs.docs || []) {

            const id = String(d?.id || "");

            if (id) addDelete(collectionName, id);

          }

        } catch {

          // ignore missing index / query failures in debug cleanup

        }

      };


      const cascadeFromFlow = async (flowId: string, targetIdMaybe?: string) => {

        addDelete("approvalFlows", flowId);

        if (targetIdMaybe) addDelete("approval_requests", targetIdMaybe);

        await deleteByQuery("approval_requests", "approvalFlowId", flowId);

        await deleteByQuery("approval_requests", "flowId", flowId);

        await deleteByQuery("todos", "approvalFlowId", flowId);

        await deleteByQuery("todos", "flowId", flowId);

      };


      const cascadeFromRequest = async (requestId: string, flowIdMaybe?: string) => {

        addDelete("approval_requests", requestId);

        if (flowIdMaybe) {

          addDelete("approvalFlows", flowIdMaybe);

          await deleteByQuery("approvalFlows", "targetId", requestId);

        } else {

          await deleteByQuery("approvalFlows", "targetId", requestId);

        }

        await deleteByQuery("todos", "approvalRequestId", requestId);

        await deleteByQuery("todos", "requestId", requestId);

        await deleteByQuery("todos", "targetId", requestId);

      };


      for (const g of chosen) {

        const id = String(g.id);

        const targetId = g.targetId ? String(g.targetId) : undefined;


        if (g.collection === "approvalFlows") {

          await cascadeFromFlow(id, targetId);

          continue;

        }

        if (g.collection === "approval_requests") {

          let flowIdMaybe: string | undefined;

          try {

            const snap = await (db as any).collection("approval_requests").doc(id).get();

            const raw = snap?.data?.() || snap?.data || {};

            flowIdMaybe = String(raw?.approvalFlowId || raw?.flowId || "") || undefined;

          } catch {}

          await cascadeFromRequest(id, flowIdMaybe);

          continue;

        }

        if (g.collection === "todos") {

          addDelete("todos", id);

          let flowIdMaybe: string | undefined;

          let requestIdMaybe: string | undefined;

          try {

            const snap = await (db as any).collection("todos").doc(id).get();

            const raw = snap?.data?.() || snap?.data || {};

            flowIdMaybe = String(raw?.approvalFlowId || raw?.flowId || "") || undefined;

            requestIdMaybe = String(raw?.approvalRequestId || raw?.requestId || raw?.targetId || "") || undefined;

          } catch {}

          if (flowIdMaybe) await cascadeFromFlow(flowIdMaybe, requestIdMaybe);

          if (requestIdMaybe) await cascadeFromRequest(requestIdMaybe, flowIdMaybe);

          continue;

        }


        addDelete(g.collection, id);

      }


      const CHUNK = 450;

      let committed = 0;

      for (let i = 0; i < ops.length; i += CHUNK) {

        const slice = ops.slice(i, i + CHUNK);

        const batch = (db as any).batch();

        for (const op of slice) {

          batch.delete((db as any).collection(op.collectionName).doc(op.id));

        }

        await batch.commit();

        committed += slice.length;

        if (ops.length > CHUNK) toast(`刪除進度：${committed}/${ops.length}`, "info");

      }


      if (ops.length) toast(`已刪除：${chosen.length} 筆幽靈（實際刪除 ${ops.length} 份文件，含關聯清理）`, "success");

      else toast("沒有可刪除的項目", "info");


      try {

        await triggerRebuildTrueTodoKpiSummaryNow();

      } catch {}


      setSelectedGhostKeys({});

      await loadAll();

    } catch (e: any) {

      setError(e?.message || String(e));

      toast(`刪除失敗：${e?.message || String(e)}`, "error");

    } finally {

      setLoading(false);

    }

  }


  async function deleteSelectedGhosts() {
    const keys = Object.keys(selectedGhostKeys).filter((k) => selectedGhostKeys[k]);
    await deleteGhostsByKeys(keys);
  }

  async function deleteSingleGhost(key: string) {
    await deleteGhostsByKeys([key]);
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredByUser = useMemo(() => {
    const mk = (t: any) => `${String(t?.docNo || "")} ${String(t?.title || "")} ${normalizeModule(t?.module)}`;
    const f = (arr: TrueTodoItem[], keyword: string) => {
      const k = String(keyword || "").trim().toLowerCase();
      if (!k) return arr;
      return arr.filter((t) => mk(t).toLowerCase().includes(k));
    };
    return {
      kevin: f(byUser.kevin, q.kevin),
      ops: f(byUser.ops, q.ops),
      gm: f(byUser.gm, q.gm),
    };
  }, [byUser, q]);

  const excludedStats = useMemo(() => {
    const byReason: Record<string, number> = {};
    for (const x of excluded || []) {
      const r = String((x as any)?.reason || "UNKNOWN");
      byReason[r] = (byReason[r] || 0) + 1;
    }
    return Object.entries(byReason).sort((a, b) => b[1] - a[1]);
  }, [excluded]);

  return (
    <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>三人待辦對帳 Debug（嚴格版：只算簽核流轉）</h3>

        <button className="btn" onClick={loadAll} disabled={loading || rebuilding}>
          {loading ? "載入中…" : "重新載入"}
        </button>

        <button className="btn" onClick={rebuildServer} disabled={loading || rebuilding}>
          {rebuilding ? "重建中…" : "重建伺服器 KPI"}
        </button>

        <StatChip label="結果" value={diffSummary.ok ? "PASS" : "FAIL"} tone={diffSummary.ok ? "good" : "bad"} />
        <StatChip label="嚴格待辦總數" value={strictCounts.total} />
        <StatChip label="approval_requests(PENDING)" value={Object.values(approvalPendingCounts || {}).reduce((a, b) => a + n(b), 0)} />

        {diffSummary.worst.module ? (
          <StatChip
            label={`最大差異：${moduleLabel(diffSummary.worst.module)}`}
            value={`${diffSummary.worst.diff > 0 ? "+" : ""}${diffSummary.worst.diff}（嚴格 ${diffSummary.worst.strict} vs request ${diffSummary.worst.requests}）`}
            tone={diffSummary.ok ? "neutral" : "bad"}
          />
        ) : null}
      </div>

      {error ? (
        <div style={{ border: "1px solid #a33b3b", background: "#2b0f0f", color: "#ffb6b6", padding: 10, borderRadius: 10 }}>
          {error}
        </div>
      ) : null}

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ border: "1px solid #333", borderRadius: 12, padding: 12, background: "#141414" }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <b>嚴格三人待辦（client 即時計算）</b>
            <StatChip label="法務（你）" value={strictCounts.countsByUser.kevin} />
            <StatChip label="營運長" value={strictCounts.countsByUser.ops} />
            <StatChip label="總經理" value={strictCounts.countsByUser.gm} />
          </div>

          <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {(Object.keys(strictCounts.countsByModule) as PendingModule[]).map((m) => (
              <div key={m} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "6px 8px", border: "1px solid #2a2a2a", borderRadius: 10 }}>
                <span style={{ opacity: 0.85 }}>{moduleLabel(m)}</span>
                <b>{fmt(strictCounts.countsByModule[m])}</b>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75, lineHeight: 1.6 }}>
            對帳邏輯：排除 RAW_TODO、排除無 targetUsers、排除未知模組（避免 UUID 舊檔/測試檔/已刪檔造成幽靈待辦）。
          </div>
        </div>

        <div style={{ border: "1px solid #333", borderRadius: 12, padding: 12, background: "#141414" }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <b>伺服器 KPI（dashboard_todos_kpi_summary）</b>
            <StatChip label="更新時間" value={serverKpi?.updatedAt ? "ok" : "—"} />
            <StatChip label="總計" value={serverKpi?.totals?.total ?? 0} />
          </div>

          <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {(["kevin", "ops", "gm"] as UserKey[]).map((k) => (
              <div key={k} style={{ border: "1px solid #2a2a2a", borderRadius: 10, padding: 10 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{userLabel(k)}</div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ opacity: 0.8 }}>總待辦</span>
                  <b>{fmt(serverKpi?.users?.[k]?.total ?? 0)}</b>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ opacity: 0.8 }}>合約</span>
                  <b>{fmt(serverKpi?.users?.[k]?.contracts ?? 0)}</b>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ opacity: 0.8 }}>用印</span>
                  <b>{fmt(serverKpi?.users?.[k]?.seals ?? 0)}</b>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ opacity: 0.8 }}>其他</span>
                  <b>{fmt(serverKpi?.users?.[k]?.others ?? 0)}</b>
                </div>
              </div>
            ))}
          </div>

          {serverKpi?.meta?.notes ? (
            <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75, lineHeight: 1.6 }}>{String(serverKpi.meta.notes)}</div>
          ) : null}
        </div>
      </div>

      {/* Per-user detailed list */}
      <div style={{ border: "1px solid #333", borderRadius: 12, padding: 12, background: "#141414" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <b>嚴格待辦明細（編號 / 名稱）</b>
          <span style={{ fontSize: 12, opacity: 0.75 }}>只列入「簽核流程」應流轉的待辦</span>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          {(["kevin", "ops", "gm"] as UserKey[]).map((uk) => (
            <div key={uk} style={{ border: "1px solid #2a2a2a", borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", minHeight: 260 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                <div style={{ fontWeight: 800 }}>{userLabel(uk)}</div>
                <StatChip label="件數" value={filteredByUser[uk].length} />
              </div>

              <input
                value={q[uk]}
                onChange={(e) => setQ((p) => ({ ...p, [uk]: e.target.value }))}
                placeholder="搜尋：編號 / 名稱 / 模組"
                style={{
                  marginTop: 8,
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid #333",
                  background: "#0f0f0f",
                  color: "#fff",
                  outline: "none",
                  fontSize: 12,
                }}
              />

              <div style={{ marginTop: 10, overflow: "auto", flex: 1 }}>
                {filteredByUser[uk].length ? (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ textAlign: "left", borderBottom: "1px solid #2a2a2a" }}>
                        <th style={{ padding: "6px 4px", opacity: 0.8 }}>模組</th>
                        <th style={{ padding: "6px 4px", opacity: 0.8 }}>編號</th>
                        <th style={{ padding: "6px 4px", opacity: 0.8 }}>名稱</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredByUser[uk].map((t) => {
                        const mod = classifyModuleStrict((t as any)?.module || (t as any)?.moduleKey || (t as any)?.sourceModule);
                        return (
                          <tr key={String((t as any)?.key || (t as any)?.id)} style={{ borderBottom: "1px dashed #262626" }}>
                            <td style={{ padding: "6px 4px", whiteSpace: "nowrap", opacity: 0.85 }}>
                              {mod ? moduleLabel(mod) : normalizeModule((t as any)?.module)}
                            </td>
                            <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>{String((t as any)?.docNo || "")}</td>
                            <td style={{ padding: "6px 4px" }}>{String((t as any)?.title || "")}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ fontSize: 12, opacity: 0.7, padding: 8 }}>（無）</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Ghost cleanup */}
      <div style={{ border: "1px solid #333", borderRadius: 12, padding: 12, background: "#141414" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <b>幽靈待辦候選清單</b>
            <StatChip label="候選" value={ghostCandidates.length} />
            <StatChip label="excluded" value={excluded.length} />
            <span
              style={{
                fontSize: 11,
                padding: "2px 8px",
                borderRadius: 999,
                border: "1px solid rgba(255,255,255,0.18)",
                opacity: 0.9,
              }}
            >
              刪除鎖：{deleteUnlocked ? "UNLOCKED" : "LOCKED"}
            </span>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {!deleteUnlocked ? (
              <button className="btn" onClick={() => ensureDeleteUnlocked()} disabled={loading || rebuilding}>
                解鎖刪除
              </button>
            ) : (
              <button className="btn" onClick={lockDeleteMode} disabled={loading || rebuilding}>
                鎖回
              </button>
            )}

            <button
              className="btn"
              onClick={() => {
                const next: Record<string, boolean> = {};
                ghostCandidates.forEach((g) => {
                  if (g.autoSafe) next[g.key] = true;
                });
                setSelectedGhostKeys(next);
              }}
              disabled={loading || rebuilding || !ghostCandidates.length}
            >
              全選安全
            </button>
            <button
              className="btn"
              onClick={() => {
                const next: Record<string, boolean> = {};
                ghostCandidates.forEach((g) => {
                  if (!isDemoId(g.id) && !isDemoId(g.targetId)) next[g.key] = true;
                });
                setSelectedGhostKeys(next);
              }}
              disabled={loading || rebuilding || !ghostCandidates.length}
            >
              全選（略過 DEMO）
            </button>
            <button className="btn" onClick={() => setSelectedGhostKeys({})} disabled={loading || rebuilding}>
              清空
            </button>

            <button
              className="btn"
              onClick={deleteSelectedGhosts}
              disabled={loading || rebuilding || !ghostCandidates.some((g) => selectedGhostKeys[g.key])}
              title={deleteUnlocked ? "刪除已勾選" : "先按『解鎖刪除』"}
            >
              刪除已勾選（高風險）
            </button>
          </div>
        </div>

        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75, lineHeight: 1.6 }}>
          預設只會自動勾選「較安全」候選：RAW_TODO，以及 approvalFlows 的 MISSING_REQUEST / MISSING_TARGET_ID。
          其他候選請你先用理由與資料內容確認後再勾。
        </div>

        <div style={{ marginTop: 10, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #2a2a2a" }}>
                <th style={{ padding: "6px 4px" }} />
                <th style={{ padding: "6px 4px", opacity: 0.8 }}>collection</th>
                <th style={{ padding: "6px 4px", opacity: 0.8 }}>id</th>
                <th style={{ padding: "6px 4px", opacity: 0.8 }}>reason</th>
                <th style={{ padding: "6px 4px", opacity: 0.8 }}>編號</th>
                <th style={{ padding: "6px 4px", opacity: 0.8 }}>名稱</th>
                <th style={{ padding: "6px 4px", opacity: 0.8, textAlign: "right" }}>刪除</th>
              </tr>
            </thead>
            <tbody>
              {ghostCandidates.map((g) => (
                <tr key={g.key} style={{ borderBottom: "1px dashed #262626" }}>
                  <td style={{ padding: "6px 4px", width: 28 }}>
                    <input
                      type="checkbox"
                      checked={!!selectedGhostKeys[g.key]}
                      onChange={(e) => setSelectedGhostKeys((p) => ({ ...p, [g.key]: e.target.checked }))}
                    />
                  </td>
                  <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>{g.collection}</td>
                  <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>{g.id}</td>
                  <td style={{ padding: "6px 4px" }}>
                    <div style={{ fontWeight: 700, color: g.autoSafe ? "#8bd9a6" : "#ffd27a" }}>{g.reason || "-"}</div>
                    {g.message ? <div style={{ opacity: 0.75, marginTop: 2 }}>{g.message}</div> : null}
                  </td>
                  <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>{g.docNo || "-"}</td>
                  <td style={{ padding: "6px 4px" }}>{g.title || "-"}</td>
                  <td style={{ padding: "6px 4px", textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      className="btn"
                      style={{ padding: "4px 8px", opacity: deleteUnlocked ? 1 : 0.55 }}
                      onClick={() => deleteSingleGhost(g.key)}
                      disabled={loading || rebuilding}
                      title={deleteUnlocked ? "刪除此筆" : "先按『解鎖刪除』"}
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {excludedStats.length ? (
          <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
            <b>excluded 分布：</b>{" "}
            {excludedStats.slice(0, 6).map(([r, c], idx) => (
              <span key={r}>
                {idx ? " / " : ""}
                {r}={c}
              </span>
            ))}
            {excludedStats.length > 6 ? <span> …</span> : null}
          </div>
        ) : null}
      </div>

      {/* raw compare */}
      <div style={{ border: "1px solid #333", borderRadius: 12, padding: 12, background: "#141414" }}>
        <b>（參考）同管線 dailySummary（computeTrueDashboardSummary）</b>
        <div style={{ marginTop: 8, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <StatChip label="date" value={recomputeClientDaily?.dateLabel || "-"} />
          <StatChip label="users" value={recomputeClientDaily?.users?.length || 0} />
          <StatChip label="合約" value={recomputeClientDaily?.users?.[0]?.counts?.contractReviews || 0} />
        </div>
        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
          這一段只是方便你快速判斷「儀錶板同一條計算管線」是否已把 RAW_TODO / 無 targetUsers / 未知模組排除掉。
        </div>
      </div>
    </div>
  );
};

// Named export for existing import sites (e.g. StorageDebugPage.tsx)
export { DashboardTodosDebugTab };

// Default export is kept for convenience.
export default DashboardTodosDebugTab;

